// react
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// models
import { FeesDetails, FieldType, ReferralFormType } from '@karehero/models';

// components
import { Card } from '@/components/atoms/Card/Card';
import { Title } from '@/components/atoms/Title/Title';

import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';
import { EditorFields } from '@/components/organisms/EditorFields/EditorFields';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import { breakpointValues } from '@/theme/breakpoint';

const IFA_PAYMENT_TERMS_URL =
  process.env.REACT_APP_IFA_PAYMENT_TERMS_URL ||
  'https://139578958.hs-sites-eu1.com/payment-terms';

const sjpValueFields = createFieldHelper<FeesDetails>({
  feeSelection: {
    label: '',
    ariaLabel: 'fee-selection',
    placeholder: 'Select',
    field: {
      type: FieldType.RadioGroup,
      variant: 'default',
      maxColumns: 1,
      options: [
        {
          label:
            'I, the referring Partner, agree to pay the fee for the Concierge Service',
          value: 'partner-paying-fee',
        },
        {
          label:
            'My client will be paying the fee for this service and is aware of the cost',
          value: 'client-paying-fee',
        },
        {
          label: 'Time Client',
          value: 'time-client',
        },
      ],
    },
  },
});

const ifaValueFields = createFieldHelper<FeesDetails>({
  feeSelection: {
    label: '',
    ariaLabel: 'fee-selection',
    placeholder: 'Select',
    field: {
      type: FieldType.RadioGroup,
      variant: 'default',
      maxColumns: 1,
      options: [
        {
          label:
            'I, the referring advisor, agree to pay the fee for this referral',
          value: 'advisor-paying-fee',
        },
        {
          label:
            'My client will be paying the fee for this service and is aware of the cost',
          value: 'client-paying-fee',
        },
      ],
    },
  },
});

const discountFields = createFieldHelper<FeesDetails>({
  discountCode: {
    label: 'If you have a discount code, please enter it here.',
    placeholder: '1234',
    field: {
      type: FieldType.Text,
    },
  },
});

const Wrap = ({ children }: { children: ReactNode }) => {
  const [width] = useWindowSize();

  if (width >= breakpointValues.mdValue) {
    return <>{children}</>;
  } else {
    return <InnerFeeContainer>{children}</InnerFeeContainer>;
  }
};

const SJPFeesDescription = ({
  value,
  setValue,
  isNoTitle = false,
}: FeesDescriptionProps) => {
  return (
    <Wrapper>
      {!isNoTitle && <Title isNoMargin>KareHero Fees</Title>}
      <FeeContainer>
        <Wrap>
          <Card
            key='fee-description'
            color='lime'
            isBordered={false}
            isPaddingSm
          >
            <FeePanel>
              <SubTitle>KareHero Care Concierge Fees as follows:</SubTitle>
              <StyledList>
                <li>Full Service: £500 + VAT</li>
                <li>Fund-Only Service: £250 + VAT</li>
              </StyledList>
              <Description>
                Both cover 12 months of service access with optional renewal.
              </Description>
            </FeePanel>
          </Card>
          <Card key='fee-payer' color='lime' isBordered={false} isPaddingSm>
            <FeePanel>
              <SubTitle>
                Who will pay for the service?
                <StyledIsRequired>{'*'}</StyledIsRequired>
              </SubTitle>
              <EditorFields
                fields={sjpValueFields}
                value={value}
                onChange={(f) =>
                  setValue({ ...value, feeSelection: f.feeSelection })
                }
              />
            </FeePanel>
          </Card>
        </Wrap>
        <Card key='fee-discount' color='lime' isBordered={false} isPaddingSm>
          <FeePanel>
            <SubTitle>Discount Code</SubTitle>
            <EditorWrapper>
              <EditorFields
                fields={discountFields}
                value={value}
                onChange={(f) =>
                  setValue({ ...value, discountCode: f.discountCode })
                }
              />
            </EditorWrapper>
          </FeePanel>
        </Card>
      </FeeContainer>
    </Wrapper>
  );
};

const IFAFeesDescription = ({ value, setValue }: FeesDescriptionProps) => {
  return (
    <Wrapper>
      <FeeContainer>
        <Card key='fee-description' color='lime' isBordered={false} isPaddingSm>
          <FeePanel>
            <SubTitle>Payment Agreement</SubTitle>
            <Description>
              The fees and payment terms for KareHero are enclosed{' '}
              <StyledLink
                to={IFA_PAYMENT_TERMS_URL}
                target='_blank'
                rel='noopener noreferrer'
              >
                here
              </StyledLink>
              . By completing this referral, you agree to these fees. An invoice
              will be issued upon submission and is payable within two weeks.
            </Description>
            <EditorFields
              fields={ifaValueFields}
              value={value}
              onChange={(f) =>
                setValue({ ...value, feeSelection: f.feeSelection })
              }
            />
            <EditorFields
              fields={discountFields}
              value={value}
              onChange={(f) =>
                setValue({ ...value, discountCode: f.discountCode })
              }
            />
          </FeePanel>
        </Card>
      </FeeContainer>
    </Wrapper>
  );
};

/**
 * FeesDescription renders the fee description page.
 */
export const FeesDescription = ({
  formType,
  ...props
}: FeesDescriptionProps) => {
  if (formType === ReferralFormType.SjpIFA) {
    return <SJPFeesDescription formType={formType} {...props} />;
  }

  return <IFAFeesDescription formType={formType} {...props} />;
};

// styles

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
`;

const EditorWrapper = styled.div`
  max-width: 400px;
  min-width: 200px;
  margin-right: ${(props) => props.theme.spacing[16]};
`;

const FeeContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing[24]};
  ${(props) =>
    props.theme.breakpoint.md(css`
      flex-direction: row;
      align-items: stretch;
    `)}
`;

const InnerFeeContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing[24]};
  ${(props) =>
    props.theme.breakpoint.sm(css`
      flex-direction: row;
      align-items: stretch;
    `)}
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[100]};
  text-align: left;
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  text-align: left;
`;

const FeePanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  gap: ${(props) => props.theme.spacing[8]};
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  margin: 0;
  padding: ${(props) => props.theme.spacing[16]};
  padding-top: 0;
  padding-bottom: 0;
`;

const StyledIsRequired = styled.span`
  margin-left: ${(props) => props.theme.spacing[2]};
  color: ${(props) => props.theme.color.danger[60]};
`;

const StyledLink = styled(Link)`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  text-decoration: underline;
`;

// types
interface FeesDescriptionProps {
  value: Partial<FeesDetails>;
  setValue: (value: Partial<FeesDetails>) => void;
  isNoTitle?: boolean;
  formType: ReferralFormType;
}

FeesDescription.displayName = 'FeesDescription';

export default FeesDescription;
